import React, { Suspense } from "react";
import { Outlet } from "react-router-dom";
import { Container, ContainerProps } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";

import { PageSkeleton } from "../../../components/loading/PageSkeleton";
import { polyfillCSS } from "../../../utils/css";
import {
  LibraryPrivacy,
  LibrarySort,
} from "@growth-machine-llc/stridist-api-client";
import { LibraryPrivacyFilter } from "../../../constants";

type ProviderProps<T> = {
  value: T;
  children: React.ReactNode;
};

type ContextProvider<T> = React.ComponentType<ProviderProps<T>>;

type MaxWidth = ContainerProps["maxWidth"] | string;

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(11),
    paddingLeft: polyfillCSS(
      `calc(${theme.spacing(4)} + var(--safe-area-inset-left)) !important`,
    ),
    [theme.breakpoints.down("lg")]: {
      paddingLeft: "20px !important",
      paddingRight: "20px !important",
    },
  },
  header: {
    marginBottom: theme.spacing(2),
  },
}));

export interface CoachLibraryLayoutProps<T> {
  Provider: ContextProvider<T>;
  providerValue: T;
  header?: React.ReactNode;
  hideHeader?: boolean;
  maxWidth?: MaxWidth;
  containerClassName?: string;
}

export interface BaseLibraryFilterContextValues {
  query: string;
  sortKey: LibrarySort;
  page: number;
  rowsPerPage: number;
  privacy: LibraryPrivacyFilter;
  handleQueryChange: (query: string) => void;
  handleSortKeyChange: (sortKey: LibrarySort) => void;
  handlePageChange: (page: number) => void;
  handleRowsPerPageChange: (rowsPerPage: number) => void;
  handleChangePrivacy: (privacy: LibraryPrivacyFilter) => void;
}

export function CoachLibraryLayout<T>(props: CoachLibraryLayoutProps<T>) {
  const {
    Provider,
    providerValue,
    header,
    hideHeader = false,
    maxWidth = "xl",
    containerClassName,
  } = props;
  const s = useStyles();

  return (
    <Provider value={providerValue}>
      <Container
        className={clsx(s.root, containerClassName)}
        maxWidth={maxWidth as ContainerProps["maxWidth"]}
      >
        {!hideHeader && header && <div className={s.header}>{header}</div>}
        <Suspense fallback={<PageSkeleton fullWidth />}>
          <Outlet />
        </Suspense>
      </Container>
    </Provider>
  );
}

export const useCoachLibraryLayoutStyles = useStyles;
